// Browse-level state shared across Search, Binders, and Groups pages.
// Persists group filter selection when switching between tabs, and the
// per-server subgroup selection (web subgroup picker spec) across visits.
// The pages call these helpers instead of re-deriving the selection.

import { groupsState } from './groups.svelte.js';
import {
  defaultSubgroupSelection,
  effectiveSubgroupSelection,
  formatSubParam,
  parseSubParam,
} from '../logic/groups-logic.js';

const STORAGE_KEY = 'bb.subgroupSelection';

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStored(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable: selection lives for the session only */
  }
}

export const browseState = $state({
  /** @type {'all' | 'mine' | string} groupId filter — 'all', 'mine', or a specific groupId */
  selectedGroupId: 'all',
  /** @type {Record<string, string[]>} server groupId → checked subgroup ids (raw) */
  selectedSubgroupIds: readStored(),
});

/**
 * Checked ids for a server group: stored if the user ever touched it,
 * otherwise their own subgroups (the bot's Effective Reach).
 * @param {import('../types.js').UserGroup} group
 * @returns {string[]}
 */
export function getSubgroupSelection(group) {
  const stored = browseState.selectedSubgroupIds[group.groupId];
  return Array.isArray(stored) ? stored : defaultSubgroupSelection(group);
}

/**
 * @param {string} groupId
 * @param {string[]} ids
 * @param {boolean} [persist] - false keeps the selection in memory for this
 *   visit only (spec Decision 3: a URL `sub` wins "for that visit", not
 *   forever) without touching localStorage. `syncSelectionFromRoute` passes
 *   `false`; a genuine user toggle (`toggleSubgroup`) uses the default.
 */
export function setSubgroupSelection(groupId, ids, persist = true) {
  // No-op when the content is unchanged: a fresh object here (even with
  // identical contents) is a real change to every `$state` reader keyed off
  // `browseState.selectedSubgroupIds` (activeSubgroupIds/scopeParams/etc.),
  // so an echoed write — e.g. the URL round-trip through
  // syncSelectionFromRoute — would otherwise re-fire every downstream
  // fetch effect that reads it, even though nothing actually changed. A
  // selection is a SET, not a sequence — `toggleSubgroup` appends, so
  // `['qn']` toggled to include 'bk' stores `['qn','bk']`, while the URL
  // mirror re-derives ids in the group's subgroup order ('bk,qn') and
  // echoes them back as `['bk','qn']`. Same set, different order, so the
  // comparison has to be order-insensitive: with equal lengths and no
  // duplicates on either side (parseSubParam/callers already dedupe),
  // "every incoming id is present in the stored array" is sufficient.
  const current = browseState.selectedSubgroupIds[groupId];
  if (
    Array.isArray(current) &&
    current.length === ids.length &&
    ids.every((id) => current.includes(id))
  ) {
    return;
  }
  browseState.selectedSubgroupIds = { ...browseState.selectedSubgroupIds, [groupId]: [...ids] };
  if (persist) writeStored(browseState.selectedSubgroupIds);
}

/** @param {import('../types.js').UserGroup} group @param {string} subgroupId */
export function toggleSubgroup(group, subgroupId) {
  const current = getSubgroupSelection(group);
  const next = current.includes(subgroupId)
    ? current.filter((id) => id !== subgroupId)
    : [...current, subgroupId];
  setSubgroupSelection(group.groupId, next);
}

/**
 * The raw selected group id, or null for 'all'/'mine'. This is the ONE place
 * that spells out the 'all' | 'mine' sentinel check — `selectedServerGroup()`
 * and every other caller derive from it instead of re-spelling the check.
 * It also needs no lookup into `groupsState.userGroups` (which loads
 * asynchronously after auth) — it's available from the very first render,
 * straight off `syncSelectionFromRoute`. Callers that only need the id to
 * drive a server-scoped fetch (e.g. `getGroupBinders`) should use this
 * instead of resolving the group object, so a cold scoped deep link doesn't
 * wait on the group list to arrive.
 * @returns {string | null}
 */
export function selectedServerGroupId() {
  const id = browseState.selectedGroupId;
  return id === 'all' || id === 'mine' ? null : id;
}

/** @returns {import('../types.js').UserGroup | undefined} */
export function selectedServerGroup() {
  const id = selectedServerGroupId();
  return id ? groupsState.userGroups.find((g) => g.groupId === id) : undefined;
}

// activeSubgroupIds() memo: every caller (activeSubgroupIds/groupUrlParams
// and, through them, every page's $derived built on the selection) reads
// `groupsState.userGroups` via selectedServerGroup(). Svelte 5 deriveds
// compare with `===`, so returning a freshly-built array on every call makes
// an unchanged selection look like a change whenever `userGroups` is merely
// touched (e.g. loadUserGroups() resolving) — every downstream $effect
// re-runs and re-fetches even though the selection is byte-for-byte the
// same. Cache the last computed array against a cheap canonical key (ids
// contain no commas) and hand back the SAME instance while the key is
// unchanged; a new array is built only when the selection genuinely changes.
let lastActiveIdsKey = null;
let lastActiveIds = [];

/**
 * The selection to send with group-scoped requests: [] means whole server.
 *
 * When the selected group can't be resolved yet — `groupsState.userGroups`
 * hasn't loaded, e.g. a cold deep link like `/wants?group=srv&sub=bk` — the
 * empty/full collapse in `effectiveSubgroupSelection` can't be computed
 * without knowing the group's subgroups. Falling back to `[]` here would
 * silently report "whole server" and drop `sub` from the URL on the very
 * first render. Instead, fall back to the RAW stored ids for the selected
 * group: a conservative scope that keeps `sub` in the URL and scopes the
 * first fetch to what the link asked for. Once groups load,
 * `selectedServerGroup()` resolves and the normal collapsing path above
 * takes over.
 * @returns {string[]}
 */
export function activeSubgroupIds() {
  const group = selectedServerGroup();
  let ids;
  if (group) {
    ids = effectiveSubgroupSelection(group, getSubgroupSelection(group));
  } else {
    const id = selectedServerGroupId();
    ids = id ? (browseState.selectedSubgroupIds[id] ?? []) : [];
  }
  const key = ids.join(',');
  if (key !== lastActiveIdsKey) {
    lastActiveIdsKey = key;
    lastActiveIds = ids;
  }
  return lastActiveIds;
}

/**
 * The `group` / `sub` URL params every browse page mirrors.
 * @returns {{ group?: string, sub?: string }}
 */
export function groupUrlParams() {
  const id = selectedServerGroupId();
  if (!id) return {};
  const params = { group: id };
  const ids = activeSubgroupIds();
  if (ids.length > 0) params.sub = formatSubParam(ids);
  return params;
}

/**
 * Apply a route's `group` / `sub` params. A URL `sub` wins over storage for
 * this visit (spec Decision 3) — held in memory only (`persist: false`), so
 * opening someone else's shared link doesn't permanently overwrite the
 * viewer's own saved selection for that server; no `sub` leaves the stored
 * selection alone; no `group` leaves everything alone.
 * @param {{ group?: string, sub?: string }} params
 */
export function syncSelectionFromRoute(params) {
  if (!params?.group) return;
  browseState.selectedGroupId = params.group;
  const ids = parseSubParam(params.sub);
  if (ids.length > 0) setSubgroupSelection(params.group, ids, false);
}
